import type { VideoAspectRatio } from '@/store/slices/creativeJobs';

import { VIDEO_MODELS } from './video-models';

/**
 * The aspect ratios a video ad can be generated in, with a numeric ratio (w/h, for sizing the preview frame)
 * and its three most popular platforms shown in the picker. The set is the accurate social-video ratios:
 * 16:9 (landscape), 9:16 (vertical), 1:1 (square), 4:5 (portrait feed) and 2:3 (Pinterest). Which are OFFERED
 * depends on the model: start-frame models (Seedance, MiniMax) can produce any of these, while Veo/Omni take
 * the aspect verbatim and the provider only accepts 16:9 / 9:16 (see `aspectsForModel`).
 */
export const ASPECT_META: Record<VideoAspectRatio, { ratio: number; platform: string }> = {
  '16:9': { ratio: 16 / 9, platform: 'YouTube, Facebook, X' },
  '9:16': { ratio: 9 / 16, platform: 'TikTok, Instagram, YouTube' },
  '1:1': { ratio: 1, platform: 'Instagram, Facebook, LinkedIn' },
  '4:5': { ratio: 4 / 5, platform: 'Instagram, Facebook, LinkedIn' },
  '2:3': { ratio: 2 / 3, platform: 'Pinterest, Instagram' },
};

/** Every ratio, ordered widest-to-tallest-ish with the two most common first. Derived from ASPECT_META
 *  (its single source) so the two lists cannot drift; the key order is the display order. */
export const ALL_ASPECTS: VideoAspectRatio[] = Object.keys(ASPECT_META) as VideoAspectRatio[];

/** The ratios Veo/Omni (pass-through, provider-validated) accept. */
export const PASSTHROUGH_ASPECTS: VideoAspectRatio[] = ['16:9', '9:16'];

/** The numeric ratio (w/h) for an aspect, for sizing a preview frame. Falls back to 16:9 for any value
 *  not in ASPECT_META (e.g. a legacy aspect on a persisted draft) so it never reads `undefined.ratio`. */
export const aspectRatioValue = (aspect: VideoAspectRatio): number => (ASPECT_META[aspect] ?? ASPECT_META['16:9']).ratio;

/** The picker label for an aspect: the ratio plus its top platforms, e.g. "16:9 · YouTube, Facebook, X". */
export const aspectLabel = (aspect: VideoAspectRatio): string => `${aspect} · ${ASPECT_META[aspect].platform}`;

/** The ratios the given model can actually produce: all of them for start-frame models, else just 16:9 / 9:16. */
export const aspectsForModel = (modelId: string): VideoAspectRatio[] => {
  const model = VIDEO_MODELS.find((m) => m.id === modelId);
  return model?.derivesAspectFromFrame ? ALL_ASPECTS : PASSTHROUGH_ASPECTS;
};

/** Keep the chosen aspect if the model supports it, else snap to 16:9 (used when switching models). */
export const snapAspectToModel = (aspect: VideoAspectRatio, modelId: string): VideoAspectRatio =>
  aspectsForModel(modelId).includes(aspect) ? aspect : '16:9';
